import { SubmissionError, getFormValues } from 'redux-form';
import { replace } from 'react-router-redux';

import moment from 'moment';

import http from '../../../../shared/http';
import endpoints from '../../../../../config/endpoints';
import { createSelectOptionsFromSysEnums } from '../../../../shared/form-elements/select';

import pick from 'lodash/pick';
import forEach from 'lodash/forEach';
import find from 'lodash/find';
import difference from 'lodash/difference';

const defaultInitialState = {
	currentLanguage: 'en',
	fields: {
		title: '',
		text: '',
		type: null,
		news_offers: null,
		news_locations: null,
		relevant_till: null
	},
	mustTranslateFields: {
		title: '',
		text: ''
	}
};

const createForm = ({ namespace, stateNodeName }) => {
	// action types
	const FORM_SUBMIT_SUCCESS = `${namespace}/${stateNodeName}-submit-success`;
	const FORM_SUBMIT_ERROR = `${namespace}/${stateNodeName}-submit-error`;

	const FETCH_SUGGEST_SUCCESS = `${namespace}/fetch-suggest-success`;
	const FETCH_SUGGEST_ERROR = `${namespace}/fetch-suggest-error`;

	const FORM_CHANGE_LANGUAGE = `${namespace}/${stateNodeName}-change-language`;
	const FORM_TRANSLATION_REQUEST_SUCCESS = `${namespace}/${stateNodeName}-translation-request-success`;
	const FORM_TRANSLATION_REQUEST_ERROR = `${namespace}/${stateNodeName}-translation-request-error`;

	const TOGGLE_PROGRESS_BAR = `${namespace}/${stateNodeName}-toggle-progress-bar`;
	const RELOAD_PARENT = `${namespace}/${stateNodeName}-reload-parent`;

	// initState
	const initialState = {
		...defaultInitialState
	};

	// actions
	const actions = {
		formSubmit: formData => (dispatch, getState) => {
			const state = getState().newsEdit;
			const { form, data } = state;

			const url = data && data.id ? `${endpoints.NEWS.BASE}/${data.id}` : endpoints.NEWS.BASE;
			const method = data && data.id ? 'put' : 'post';

			const newsOffers = formData && formData.news_offers ? getValues(formData, 'news_offers') : null;
			const newsLocations = formData && formData.news_locations
				? getValues(formData, 'news_locations') : null;
			const type = formData && formData.type ? getValues(formData, 'type') : null;

			const fields = pick(formData, Object.keys(form.fields));

			const dataToSubmit = {
				...fields,
				news_offers: newsOffers,
				news_locations: newsLocations,
				type: type && type[0],
				should_publish: (!data || !data.published_at) && formData.should_publish,
				title: fields.title && fields.title.trim(),
				text: fields.text && fields.text.trim()
			};

			const request = http({
				url,
				method,
				actions: [FORM_SUBMIT_SUCCESS, FORM_SUBMIT_ERROR],
				data: dataToSubmit
			});

			return request(dispatch).then(action => {
				if (action.type === FORM_SUBMIT_SUCCESS) {
					if (action.payload && action.payload.data && action.payload.data.id) {
						dispatch(replace(`/news/edit/${action.payload.data.id}`));
					} else {
						dispatch({ type: RELOAD_PARENT });
					}
				}
				if (action.type === FORM_SUBMIT_ERROR && action.payload.response.status === 400) {
					const responseErrors = action.payload.response.data;
					throw new SubmissionError(prepareFormErrors(responseErrors));
				}
			});
		},

		offersLookups: text => dispatch => {
			const request = http({
				url: `${endpoints.LOOKUPS.OFFERS}?query=${text}`,
				method: 'get',
				actions: [FETCH_SUGGEST_SUCCESS, FETCH_SUGGEST_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_SUGGEST_SUCCESS) {
					return {
						options: createSelectOptionsFromSysEnums(action.payload.data)
					};
				}
			});
		},

		locationsLookup: text => dispatch => {
			const request = http({
				url: `${endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC}?query=${text}`,
				method: 'get',
				actions: [FETCH_SUGGEST_SUCCESS, FETCH_SUGGEST_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_SUGGEST_SUCCESS) {
					return {
						options: createSelectOptionsFromSysEnums(action.payload.data)
					};
				}
			});
		},

		formChangeLanguage: lang => (dispatch, getState) => {
			const { defaultLanguage, data } = getState().newsEdit;
			const actionChangeLang = { type: FORM_CHANGE_LANGUAGE, payload: lang };
			const request = actions.translationRequest(data.id, lang);

			if (lang === defaultLanguage) return dispatch(actionChangeLang);

			dispatch({ type: TOGGLE_PROGRESS_BAR });

			dispatch(request).then(action => {
				if (action.type === FORM_TRANSLATION_REQUEST_SUCCESS) {
					dispatch({
						...actionChangeLang,
						meta: action.payload.data
					});
				}
			});
		},

		formSubmitTranslation: formData => (dispatch, getState) => {
			const { form: { mustTranslateFields, currentLanguage }, data } = getState().newsEdit;

			const fields = pick(
				{
					...mustTranslateFields,
					...formData
				},
				Object.keys(mustTranslateFields)
			);

			const request = actions.translationRequest(data.id, currentLanguage, 'put', fields);

			return dispatch(request).then(action => {
				if (action.type === FORM_TRANSLATION_REQUEST_SUCCESS) {
					dispatch(actions.formChangeLanguage(currentLanguage));
				}

				if (action.type === FORM_TRANSLATION_REQUEST_ERROR && action.payload.response.status === 400) {
					const responseErrors = action.payload.response.data;
					throw new SubmissionError(prepareFormErrors(responseErrors));
				}
			});
		},

		translationRequest: (id, lang, method = 'get', data = {}) => dispatch => {
			const url = endpoints.NEWS.BASE_TRANSLATION.replace('{id}', id).replace('{lang}', lang);
			return http({
				url,
				method,
				actions: [FORM_TRANSLATION_REQUEST_SUCCESS, FORM_TRANSLATION_REQUEST_ERROR],
				data
			})(dispatch);
		}
	};

	// reducers
	const reducers = {
		[FORM_CHANGE_LANGUAGE]: (state, action) => {
			const { payload, meta } = action;

			return {
				...state,
				showProgressBar: false,
				form: {
					...state.form,
					currentLanguage: payload,
					mustTranslateFields: Object.assign(
						{},
						state.form.mustTranslateFields,
						meta ? meta : initialState.mustTranslateFields
					)
				}
			};
		},
		[FORM_SUBMIT_SUCCESS]: state => ({ ...state, form: initialState }),
		[FORM_SUBMIT_ERROR]: errorReducerFn,
		[FETCH_SUGGEST_ERROR]: errorReducerFn,
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
		[RELOAD_PARENT]: state => ({ ...state, data: null, showProgressBar: false })
	};

	// selectors
	const selectors = {
		getFormData: state => {
			const { data, form: { mustTranslateFields, currentLanguage }, defaultLanguage } = state.newsEdit;
			const publishedAt = data && data.published_at;
			const shouldTranslate = defaultLanguage != currentLanguage;
			const translateMixinFields = shouldTranslate ? mustTranslateFields : {};

			const typesSelectOptions = createSelectOptionsFromSysEnums(state.root.lists['news-types']);

			const currentFormValues = getFormValues('newsEditForm')(state);
			const isShouldPublishChecked = currentFormValues && currentFormValues.should_publish;
			const defaultNewsType = typesSelectOptions && [find(typesSelectOptions, { value: 'custom' })];
			const dataNewsType = data && data.type && typesSelectOptions && [find(typesSelectOptions, { value: data.type })];

			return {
				shouldTranslate,
				selectOptions: {
					types: typesSelectOptions
				},
				disabledFields: [
					...getTranslateDisabledFields(shouldTranslate, state.newsEdit.data, mustTranslateFields),
					publishedAt || shouldTranslate ? 'should_publish' : null,
					publishedAt || shouldTranslate ? 'relevant_till' : null
				],
				initialValues: {
					...data,
					...translateMixinFields,
					type: dataNewsType || defaultNewsType,
					should_publish: !!publishedAt,
					relevant_till: data && data.relevant_till ||  moment().add('1', 'week').format('YYYY-MM-DD')
				},
				isShouldPublishChecked
			};
		}
	};

	return {
		initialState,
		actions,
		reducers,
		selectors
	};
};

// functions
const prepareFormErrors = errors => {
	const validationsErrors = {};
	forEach(errors, value => {
		const key = value.dataPath.slice(1);
		validationsErrors[key] = value.message;
	});
	return validationsErrors;
};

const errorReducerFn = (state, action) => {
	if (action.payload.response.status !== 400) {
		return { ...state, error: true, showProgressBar: false };
	}
	return state;
};

const getValues = (data, key) => {
	return data && data[key] && data[key].length && data[key].map(option => option.value);
};

const getTranslateDisabledFields = (shouldTranslate, data, mustTranslateFields) => {
	return (shouldTranslate && difference(Object.keys(data), Object.keys(mustTranslateFields))) || [];
};

export default createForm;



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/news/components/form/large/index.js